import { useState } from 'react'

import type { ProfilePlan } from '../../api/profile'
import { useSubscriptionPlanAction } from '../../hooks/useSubscriptionPlanAction'
import { hasActiveSubscription } from '../../lib/subscriptionPlanAction'
import { SubscriptionPlansModal } from './SubscriptionPlansModal'

type Props = {
  angular: boolean
  plan: ProfilePlan
  plansEnabled?: boolean
  onPlanChanged?: () => void
}

function planShowsExpiry(plan: ProfilePlan): boolean {
  return hasActiveSubscription(plan)
}

function planHint(plan: ProfilePlan, action: ReturnType<typeof useSubscriptionPlanAction>): string {
  if (action === 'purchase') {
    return '开通套餐可获得积分与创作权益'
  }
  if (action === 'upgrade') {
    const pct = Math.min(100, Math.round((plan.balanceCredits / Math.max(plan.monthlyQuota, 1)) * 100))
    return `本月剩余约 ${pct}% 套餐积分，可升级获取更高配额`
  }
  if (hasActiveSubscription(plan)) {
    return '当前已是可购最高档套餐'
  }
  return '积分用完后可联系管理员开通套餐'
}

export function ProfilePlanStrip({ angular, plan, plansEnabled = true, onPlanChanged }: Props) {
  const [modalOpen, setModalOpen] = useState(false)
  const action = useSubscriptionPlanAction(plan, plansEnabled)
  const showExpiry = planShowsExpiry(plan)
  const pct = Math.min(100, Math.round((plan.balanceCredits / Math.max(plan.monthlyQuota, 1)) * 100))

  return (
    <>
      <div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-ink-soft">
              订阅
            </span>
            <span
              className={[
                'border px-2 py-0.5 text-[11px] font-semibold text-accent',
                angular ? 'rounded-none' : 'rounded-md',
              ].join(' ')}
              style={{
                borderColor: 'color-mix(in srgb, var(--accent) 24%, var(--line))',
                backgroundColor: 'color-mix(in srgb, var(--accent) 10%, var(--surface-muted))',
              }}
            >
              当前套餐
            </span>
          </div>
          <div className="mt-2 flex flex-wrap items-baseline gap-x-3 gap-y-1">
            <h3 className="text-lg font-semibold text-ink sm:text-xl">{plan.planName}</h3>
            {showExpiry ? (
              <span className="text-sm text-ink-soft">
                到期 <span className="font-medium text-ink">{plan.expiresAt}</span>
              </span>
            ) : null}
          </div>
        </div>
        <div className="w-full min-w-0 lg:max-w-md lg:flex-1">
          <div className="flex justify-between text-xs font-medium text-ink">
            <span>剩余积分</span>
            <span className="tabular-nums">
              {plan.balanceCredits.toLocaleString('zh-CN')} / {plan.monthlyQuota.toLocaleString('zh-CN')}
            </span>
          </div>
          <div
            className={[
              'mt-2 h-2.5 overflow-hidden bg-[var(--menu-solid)] ring-1 ring-line',
              angular ? 'rounded-none' : 'rounded-full',
            ].join(' ')}
            role="presentation"
          >
            <div
              className={[
                'h-full bg-accent transition-[width]',
                angular ? 'rounded-none' : 'rounded-full',
              ].join(' ')}
              style={{ width: `${pct}%` }}
            />
          </div>
          <p className="mt-2 text-xs text-ink-soft">{planHint(plan, action)}</p>
        </div>
        {action ? (
          <button
            type="button"
            onClick={() => setModalOpen(true)}
            className={[
              'inline-flex shrink-0 items-center justify-center border-2 border-accent bg-panel px-4 py-2.5 text-sm font-semibold text-accent transition hover:bg-[var(--select-active-bg)]',
              angular ? 'rounded-none' : 'rounded-full',
            ].join(' ')}
          >
            {action === 'purchase' ? '购买套餐' : '升级套餐'}
          </button>
        ) : null}
      </div>

      {action ? (
        <SubscriptionPlansModal
          open={modalOpen}
          mode={action}
          angular={angular}
          onClose={() => setModalOpen(false)}
          onPurchaseSuccess={() => {
            onPlanChanged?.()
          }}
        />
      ) : null}
    </>
  )
}
